/**
 * <kit-run-tree> — observability as a framework primitive (RFC AX-8), ported
 * from the original studio's run-tree (recon-01 §AX snippet 3): any captured
 * Run renders as a `provider · model · latency · tokens · cost` glance
 * (<details>/<summary>), opening to a typed <dl class="run-metrics">, nested
 * child runs recursively (keyed `repeat` per §3a), and a
 * `<button data-act="open-raw" data-run="…">` drill.
 *
 * Input: the pragmatic v1 `RunNode` shape defined here — apps map their run
 * capture (e.g. career's run.Captured / RunStep) onto it in Phase 4. Data
 * arrives via the `run` PROPERTY (not an attribute — it is a payload, not
 * identity); labels via the `labels` property with German defaults (AX-9:
 * structure English, text de-DE). Numbers format de-DE (2,4 s · 3.812 Tok ·
 * $0,004). No i18n import — a `labels` object is the whole seam.
 *
 * The drill: `open-raw` dispatches a composed, bubbling
 * `kit-run-tree:open-raw` CustomEvent with `{ id, node }` — the app decides
 * what a raw-call drill looks like (dialog, route, …).
 *
 * Registration is explicit via `defineRunTree()` (no import side effects).
 * Vocabulary: the `open-raw` verb (payload-bearing) lives in KIT_VOCABULARY;
 * the element must stay self-clean under `auditAx` (GATE 2 criterion).
 */

import { signal, type ReadonlySignal, type Signal } from "../core/signal";
import { KitElement } from "../core/element";
import type { ActionMap } from "../core/actions";
import type { ViewModel } from "../core/viewmodel";
import { html, nothing, repeat, type TemplateResult } from "../render/html";

export interface RunTokens {
  input: number;
  output: number;
  cacheRead?: number;
}

/** Pragmatic v1 run shape; apps map their capture onto it (Phase 4). */
export interface RunNode {
  id: string;
  /** Optional human label for the step (e.g. "Discovery pass 2"). */
  label?: string;
  provider: string;
  model: string;
  latencyMs: number;
  tokens: RunTokens;
  costUsd: number;
  children?: RunNode[];
  /** Raw call payload for the drill; travels in the open-raw event. */
  raw?: unknown;
}

export interface RunTreeLabels {
  eyebrow: string;
  model: string;
  time: string;
  tokens: string;
  cache: string;
  cost: string;
  openRaw: string;
}

export const DEFAULT_RUN_TREE_LABELS: RunTreeLabels = {
  eyebrow: "Agent-Lauf",
  model: "Modell",
  time: "Zeit",
  tokens: "Tokens (Eingabe·Ausgabe)",
  cache: "Cache",
  cost: "Kosten",
  openRaw: "Roh-Aufruf ansehen",
};

const nf = new Intl.NumberFormat("de-DE");
const seconds = (ms: number): string =>
  `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 }).format(ms / 1000)} s`;
const usd = (v: number): string =>
  `$${new Intl.NumberFormat("de-DE", { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v)}`;
const totalTokens = ({ input, output }: RunTokens): number => input + output;

class RunTreeVm implements ViewModel {
  readonly run: Signal<RunNode | null>;
  readonly labels: Signal<RunTreeLabels>;
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>;

  constructor(run: Signal<RunNode | null>, labels: Signal<RunTreeLabels>) {
    this.run = run;
    this.labels = labels;
    this.renderDeps = [run, labels];
  }

  findNode(id: string): RunNode | null {
    const walk = (node: RunNode): RunNode | null => {
      if (node.id === id) return node;
      for (const child of node.children ?? []) {
        const hit = walk(child);
        if (hit) return hit;
      }
      return null;
    };
    const rootNode = this.run.value;
    return rootNode ? walk(rootNode) : null;
  }
}

export class KitRunTree extends KitElement<RunTreeVm> {
  // Signals live on the element so properties can be set BEFORE connect.
  readonly #run = signal<RunNode | null>(null);
  readonly #labels = signal<RunTreeLabels>(DEFAULT_RUN_TREE_LABELS);

  get run(): RunNode | null {
    return this.#run.value;
  }
  set run(node: RunNode | null) {
    this.#run.set(node);
  }

  get labels(): RunTreeLabels {
    return this.#labels.value;
  }
  set labels(partial: Partial<RunTreeLabels>) {
    this.#labels.set({ ...DEFAULT_RUN_TREE_LABELS, ...partial });
  }

  protected createViewModel(): RunTreeVm {
    return new RunTreeVm(this.#run, this.#labels);
  }

  protected render(vm: RunTreeVm): TemplateResult {
    const node = vm.run.value;
    if (!node) return html`${nothing}`;
    return this.#renderNode(node, vm.labels.value);
  }

  #renderNode(node: RunNode, labels: RunTreeLabels): TemplateResult {
    const { tokens } = node;
    const glance = [
      node.provider,
      node.model,
      seconds(node.latencyMs),
      `${nf.format(totalTokens(tokens))} Tok`,
      usd(node.costUsd),
    ].join(" · ");
    const children = node.children ?? [];
    return html`<details class="run-tree">
      <summary class="run-tree-head">
        <span class="run-tree-eyebrow"
          >${node.label ? `${labels.eyebrow} — ${node.label}` : labels.eyebrow}</span
        >
        <span class="run-tree-glance">${glance}</span>
      </summary>
      <dl class="run-metrics">
        <dt>${labels.model}</dt>
        <dd>${node.provider}/${node.model}</dd>
        <dt>${labels.time}</dt>
        <dd>${seconds(node.latencyMs)}</dd>
        <dt>${labels.tokens}</dt>
        <dd>${nf.format(tokens.input)} · ${nf.format(tokens.output)}</dd>
        ${tokens.cacheRead !== undefined
          ? html`<dt>${labels.cache}</dt>
              <dd>${nf.format(tokens.cacheRead)}</dd>`
          : nothing}
        <dt>${labels.cost}</dt>
        <dd>${usd(node.costUsd)}</dd>
      </dl>
      <button class="run-raw" data-act="open-raw" data-run=${node.id}>
        ${labels.openRaw} →
      </button>
      ${children.length > 0
        ? html`<div class="run-children">
            ${repeat(
              children,
              (child) => child.id,
              (child) => this.#renderNode(child, labels)
            )}
          </div>`
        : nothing}
    </details>`;
  }

  protected override actions(vm: RunTreeVm): ActionMap {
    return {
      "open-raw": ({ run: id }) => {
        if (!id) return;
        const node = vm.findNode(id);
        this.dispatchEvent(
          new CustomEvent("kit-run-tree:open-raw", {
            detail: { id, node },
            bubbles: true,
            composed: true,
          })
        );
      },
    };
  }
}

/** Register `<kit-run-tree>` (idempotent, explicit). */
export function defineRunTree(): void {
  if (!customElements.get("kit-run-tree")) {
    customElements.define("kit-run-tree", KitRunTree);
  }
}
